import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil, switchMap } from "rxjs/operators";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCommonModule, MatOptionModule } from "@angular/material/core";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { FinancialInformationService } from "src/app/iap/services/financial-information.service";
import { MyProfileService } from "src/app/iap/services/my-profile.service";

@Component({
    selector: 'app-wire-transfer',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatDialogModule,
        MatCommonModule,
    ],
    templateUrl: './wire-transfer.component.html',
    styleUrl: './wire-transfer.component.scss'
})
export class WireTransferComponent implements OnInit, OnDestroy {
  @Input() agencyId: string;
  @Input() paymentMethod: string = '';
  @Output() submitted = new EventEmitter<void>();
  WTForm!: FormGroup;
  financialInfoData: any;
  isSubmitting = false;
  isSubmitted = false;
  private destroy$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder, private financialInformationService: FinancialInformationService, private myProfileService: MyProfileService) {
    // this.WTForm = this.createFormGroup();
  }

  ngOnInit(): void {
    if (this.agencyId) {
      this.WTForm = this.createFormGroup();
      // Set Payment_Method from parent radio selection and lock it
      this.WTForm.get('Payment_Method')?.setValue(this.paymentMethod);
      this.WTForm.get('Payment_Method')?.disable();
    } else {
      console.error("Agency ID is not set.");
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToAgencyList(): void {
    // this.router.navigateByUrl("/agency/agencies");
    console.log("goToAgencyList clicked");
  }

  onSubmit(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.WTForm.invalid) {
      this.WTForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    console.log("agencyId: ", this.agencyId);
    console.log(this.WTForm.value);

    this.financialInformationService.submitFinancialInfo(this.WTForm.getRawValue())
      .pipe(
        switchMap((res) => {
          console.log("response: ", res);
          if (res.status === 200) {
            console.log("Successfully submitted financial information");
            return this.myProfileService.updateAgencyOnboardingStatus(
              Number(this.agencyId),
              "true",
              "step2"
            );
          }
          throw new Error("API response status was not 200");
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (result) => {
          console.log("updated onboarding status!: ", result);
          this.isSubmitting = false;
          this.isSubmitted = true;
          this.submitted.emit();
        },
        (error) => {
          console.error("Error during submission:", error);
          this.isSubmitting = false;
        }
      );
  }

  private createFormGroup(): FormGroup {
    const requiredText = [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)];
    const requiredBankId = [Validators.required, Validators.pattern(/^[0-9A-Za-z\-]+$/)];
    const requiredPhone = [Validators.required, Validators.pattern(/^\+?[0-9\s\-().]{7,30}$/)];

    return this.formBuilder.group({
      Agency_ID: [this.agencyId],
      Payment_Method: ["", Validators.required],
      Currency: ["", [Validators.required, Validators.pattern(/^[A-Za-z]{3}$/), Validators.maxLength(3)]],
      BF_Name: ["", requiredText],
      BF_Address: ["", requiredText],
      BF_Bank_Name: ["", requiredText],
      BF_Bank_Address: ["", requiredText],
      BF_Bank_Phone: ["", requiredPhone],
      BF_Bank_Account: ["", requiredBankId],
      BF_Bank_Code: ["", requiredBankId],
      BF_Bank_Transit: ["", requiredBankId],
      BF_Bank_ABA: ["", requiredBankId],
      BF_Bank_Swift: ["", requiredBankId],
      BF_Bank_IBAN: ["", requiredBankId],
      IN_Bank_Name: ["", requiredText],
      IN_Bank_Address: ["", requiredText],
      IN_Bank_Account: ["", requiredBankId],
      IN_Bank_Swift: ["", requiredBankId],
      DD_Legal_Name: [""],
      DD_HST_GST_Number: [""],
      DD_SIN: [""],
      CP_Last_Name: [""],
      CP_First_Name: [""],
      CP_Bank_Phone: [""],
      CP_Email: [""],
      AS_Last_Name: [""],
      AS_First_Name: [""],
      AS_Title: [""],
      AS_Address: [""],
      AS_Phone: [""],
      AS_Fax: [""],
    });
  }

  private populateForm(fin: any) {
    this.WTForm.patchValue({
      Payment_Method: fin.Payment_Method,
      Currency: fin.Currency,
      BF_Name: fin.BF_Name,
      BF_Address: fin.BF_Address,
      BF_Bank_Name: fin.BF_Bank_Name,
      BF_Bank_Address: fin.BF_Bank_Address,
      BF_Bank_Phone: fin.BF_Bank_Phone,
      BF_Bank_Account: fin.BF_Bank_Account,
      BF_Bank_Code: fin.BF_Bank_Code,
      BF_Bank_Transit: fin.BF_Bank_Transit,
      BF_Bank_ABA: fin.BF_Bank_ABA,
      BF_Bank_Swift: fin.BF_Bank_Swift,
      BF_Bank_IBAN: fin.BF_Bank_IBAN,
      IN_Bank_Name: fin.IN_Bank_Name,
      IN_Bank_Address: fin.IN_Bank_Address,
      IN_Bank_Account: fin.IN_Bank_Account,
      IN_Bank_Swift: fin.IN_Bank_Swift,
      DD_Legal_Name: fin.DD_Legal_Name,
      DD_HST_GST_Number: fin.DD_HST_GST_Number,
      DD_SIN: fin.DD_SIN,
      CP_Last_Name: fin.CP_Last_Name,
      CP_First_Name: fin.CP_First_Name,
      CP_Bank_Phone: fin.CP_Bank_Phone,
      CP_Email: fin.CP_Email,
      AS_Last_Name: fin.AS_Last_Name,
      AS_First_Name: fin.AS_First_Name,
      AS_Title: fin.AS_Title,
      AS_Address: fin.AS_Address,
      AS_Phone: fin.AS_Phone,
      AS_Fax: fin.AS_Fax,
    });
  }
}
